import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { AppShell } from './components/AppShell';
import { Categories } from './screens/Categories';
import { Compliance } from './screens/Compliance';
import { Login } from './screens/Login';
import { MediaLibrary } from './screens/MediaLibrary';
import { PollEditor } from './screens/PollEditor';
import { PollsList } from './screens/PollsList';
import { WhatsApp } from './screens/WhatsApp';

/**
 * Six screens (02-requirements.md §Admin Dashboard): Login, the polls list, the
 * poll editor, the media library, the categories list — and the delete
 * confirmation, which is a dialog inside the editor, not a route of its own.
 * Categories arrived 2026-08-16 with §Category Icons; it lists what exists and
 * edits one field, and deliberately offers no create and no delete.
 *
 * Query defaults: a back office is read-mutate-reread, so a stale list after a
 * write is the bug class to design out. `staleTime: 0` and an explicit
 * invalidation per mutation is that design; retry is off for mutations because
 * repeating a delete is not a retry.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 0, refetchOnWindowFocus: false, retry: 1 },
    mutations: { retry: 0 },
  },
});

/** A signed-out visitor never reaches a screen; the API would 401 it anyway. */
function RequireAdmin({ children }: { children: ReactNode }): ReactNode {
  const { admin } = useAuth();
  if (!admin) return <Navigate to="/entrar" replace />;
  return <AppShell>{children}</AppShell>;
}

export function AppRoutes(): ReactNode {
  return (
    <Routes>
      <Route path="/entrar" element={<Login />} />
      <Route
        path="/"
        element={
          <RequireAdmin>
            <PollsList />
          </RequireAdmin>
        }
      />
      <Route
        path="/enquetes/:id"
        element={
          <RequireAdmin>
            <PollEditor />
          </RequireAdmin>
        }
      />
      <Route
        path="/categorias"
        element={
          <RequireAdmin>
            <Categories />
          </RequireAdmin>
        }
      />
      <Route
        path="/imagens"
        element={
          <RequireAdmin>
            <MediaLibrary />
          </RequireAdmin>
        }
      />
      <Route
        path="/conformidade"
        element={
          <RequireAdmin>
            <Compliance />
          </RequireAdmin>
        }
      />
      <Route
        path="/whatsapp"
        element={
          <RequireAdmin>
            <WhatsApp />
          </RequireAdmin>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App(): ReactNode {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
